import { PlatformPrefix } from "../settings/platforms";
import { formatDate } from "./formatting-utils";
/**
 * Returns a hash code from a string
 * @param  {String} str The string to hash.
 * @return {String}    A string hash
 * @see http://werxltd.com/wp/2010/05/13/javascript-implementation-of-javas-string-hashcode-method/
 * @see https://stackoverflow.com/questions/6122571/simple-non-secure-hash-function-for-javascript
 * @see https://devdocs.io/openjdk~8/java/lang/string#hashCode--
 */
export function hashString(str) {
    let hash = 0;
    for (let i = 0, len = str.length; i < len; i++) {
        const chr = str.charCodeAt(i);
        hash = ((hash << 5) - hash + chr) >>> 0; // Convert to 32bit unsigned integer
    }
    return hash.toString();
}
// eslint-disable-next-line @typescript-eslint/no-empty-function
export async function noop() { }
export async function sleep(interval) {
    return new Promise((resolve) => setTimeout(resolve, interval));
}
export async function wasteCpuCycles(interval) {
    const end = performance.now() + interval;
    while (performance.now() < end) {
        Math.sqrt(Math.random());
    }
}
/**
 * Converts a minutes interval to a valid cron expression (if it evenly divides 60, 1440, or 60).
 * @param {number} minutes - The interval in minutes
 * @return {string} A valid cron expression
 */
export function getCronExpression(minutes) {
    if (minutes <= 0) {
        throw new Error("Minutes must be greater than 0");
    }
    // If it's an exact number of days
    if (minutes % 1440 === 0) {
        const days = minutes / 1440;
        return `0 0 */${days} * *`;
    }
    // If it's an exact number of hours (but less than a day)
    if (minutes % 60 === 0) {
        const hours = minutes / 60;
        return `0 */${hours} * * *`;
    }
    // If it's less than 60 minutes
    if (minutes < 60) {
        return `*/${minutes} * * * *`;
    }
    // Otherwise, a strictly "every N minutes" schedule cannot be represented
    // by a single standard cron line. Throw to let caller know.
    throw new Error(`Interval must evenly divide 60 or 1440 (i.e. a whole number of hours or days). Received: ${minutes}`);
}
export function timeQueue(func, delay) {
    const queue = [];
    let timerId = null;
    function processQueue() {
        if (queue.length === 0) {
            if (timerId !== null) {
                clearInterval(timerId);
                timerId = null;
            }
        }
        else {
            const call = queue.shift();
            call?.();
        }
    }
    return function (...args) {
        queue.push(() => func.apply(this, args));
        if (timerId === null) {
            timerId = setInterval(processQueue, delay);
        }
    };
}
export * from "./environment-utils";
export function parseProgressLog(logEntry) {
    const isoDate = logEntry.slice(0, 24);
    const timestamp = new Date(isoDate).getTime();
    const progressUpdate = JSON.parse(logEntry.slice(25));
    return [timestamp, progressUpdate];
}
export const isExchange = (x) => x.id.startsWith(PlatformPrefix.Exchange);
export const isBlockchain = (x) => x.id.startsWith(PlatformPrefix.Chain);
export function getBucketSize(timeInterval) {
    timeInterval = timeInterval.toUpperCase();
    // Seconds
    if (timeInterval === "1S")
        return 1;
    // Minutes
    if (timeInterval === "1")
        return 60;
    if (timeInterval === "3")
        return 180;
    if (timeInterval === "5")
        return 300;
    if (timeInterval === "15")
        return 900;
    if (timeInterval === "30")
        return 1800;
    // Hours
    if (timeInterval === "60")
        return 3600;
    if (timeInterval === "120")
        return 7200;
    if (timeInterval === "240")
        return 14400;
    // Days/Weeks/Months
    if (timeInterval === "1D")
        return 86400;
    if (timeInterval === "3D")
        return 3 * 86400;
    if (timeInterval === "1W")
        return 604800;
    if (timeInterval === "1M")
        return 2592000;
    throw new Error(`Unsupported time interval: ${timeInterval}`);
}
export function approximateTime(time, timeInterval) {
    const bucketSize = getBucketSize(timeInterval);
    const remainder = time % bucketSize;
    return remainder > bucketSize / 2 ? time - remainder + bucketSize : time - remainder;
}
export function floorTimestamp(timestamp, timeInterval) {
    const bucketSize = getBucketSize(timeInterval) * 1000;
    const remainder = timestamp % bucketSize;
    return timestamp - remainder;
}
export function roundTimestamp(timestamp, timeInterval) {
    const bucketSize = getBucketSize(timeInterval) * 1000;
    const remainder = timestamp % bucketSize;
    if (remainder > bucketSize / 2)
        return timestamp - remainder + bucketSize;
    return timestamp - remainder;
}
export function ensureValidBuckets(prices, timeInterval) {
    const bucketSize = getBucketSize(timeInterval);
    const patched = [];
    let prevRecord;
    for (let i = 0; i < prices.length; i++) {
        const record = prices[i];
        const intervalsDiff = prevRecord ? (record.time - prevRecord.time) / bucketSize : 0;
        if (intervalsDiff > 1) {
            // fill the gaps between data points
            for (let i = 1; i < intervalsDiff; i++) {
                const gapTime = prevRecord.time + i * bucketSize;
                patched.push({
                    time: gapTime,
                    value: prevRecord.value,
                });
            }
        }
        patched.push(record);
        prevRecord = record;
    }
    return patched;
}
/**
 * Generic pagination utility for price APIs that handle time-based pagination.
 * Automatically splits requests that exceed the page limit into multiple requests.
 *
 * @param options Configuration for pagination
 * @returns Object containing validSince timestamp and previousPage data
 */
export async function paginatePriceRequest(options) {
    const { since, until, limit, pageLimit, bucketSizeInMs, queryFn } = options;
    let validSince = since;
    let previousPage = [];
    if (since && until) {
        const records = Math.floor((until - since) / bucketSizeInMs);
        if (records > pageLimit) {
            validSince = until - pageLimit * bucketSizeInMs;
            previousPage = await queryFn(since, validSince, limit - pageLimit);
        }
    }
    return { previousPage, validSince };
}
export async function paginate(params) {
    const { since, until, window, concurrency = 10, fn, progress = noop, signal, cooldown, debugMode, } = params;
    const pages = [];
    const data = [];
    for (let start = since; start <= until; start += window) {
        const end = Math.min(start + window, until);
        pages.push(async () => {
            if (signal?.aborted)
                throw new Error(signal.reason);
            // await progress([undefined, `Fetching ${formatDate(start)} - ${formatDate(end)}`])
            try {
                const result = await fn(start, end);
                if (debugMode) {
                    await progress([
                        undefined,
                        `Fetched ${result.length} records from ${formatDate(start)} - ${formatDate(end)}`,
                    ]);
                }
                return result;
            }
            catch (error) {
                if (String(error).includes("429"))
                    throw error;
                if (debugMode) {
                    await progress([
                        undefined,
                        `Warn: skipping ${formatDate(start)} to ${formatDate(end)}. ${String(error)}`,
                    ]);
                }
                return [];
            }
        });
    }
    for (let i = 0; i < pages.length; i += concurrency) {
        if (i && cooldown)
            await sleep(cooldown);
        const page = pages.slice(i, i + concurrency);
        const results = await Promise.all(page.map((x) => x()));
        for (const result of results) {
            data.push(...result);
        }
    }
    await progress([undefined, `Fetched ${data.length} records`]);
    return data;
}
export async function paginateExact(params) {
    const { count, concurrency = 10, fn, progress = noop, signal, cooldown, debugMode } = params;
    const pages = [];
    const data = [];
    for (let i = 0; i < count; i++) {
        pages.push(async () => {
            if (signal?.aborted)
                throw new Error(signal.reason);
            try {
                const result = await fn(i);
                return result;
            }
            catch (error) {
                if (String(error).includes("429"))
                    throw error;
                if (debugMode) {
                    await progress([undefined, `Warn: skipping ${i}. ${String(error)}`]);
                }
                return [];
            }
        });
    }
    for (let i = 0; i < pages.length; i += concurrency) {
        if (i && cooldown)
            await sleep(cooldown);
        const page = pages.slice(i, i + concurrency);
        const results = await Promise.all(page.map((x) => x()));
        for (const result of results) {
            data.push(...result);
        }
    }
    await progress([undefined, `Fetched ${data.length} records`]);
    return data;
}
//# sourceMappingURL=utils.js.map